import type { FinalOwner, RequestSource, RequestStatus, RequestType } from "./dto";

/**
 * The request lifecycle as data: which statuses exist, in what order, which
 * count as open, and what each is called. The Worker (filters, SLA, timeline
 * text) and the SPA (selects, filters, labels) both read from here, so adding
 * a status is one edit plus a migration for the CHECK constraint — never a
 * hunt for hand-typed lists. The database constraint in migrations/ must
 * list the same values.
 */

/** Every status, in the order selects and filters show them. */
export const STATUS_ORDER: RequestStatus[] = ["new", "backlog", "decision", "triaged", "progress", "waiting", "validation", "done", "supported", "wontdo", "escalated"];

/** Statuses a request is still ours to resolve in: the default list filter,
 *  the SLA clock, open counts. Backlog is open — accepted, not yet scheduled,
 *  and still ours to deliver. Needs decision is open — we have looked, but
 *  not yet chosen between backlog, decline and escalation. Escalated is open
 *  too — Core Eng owns the fix, but the customer is still waiting on us.
 *  Validation is open: the work is written but unconfirmed, so the request is
 *  still ours and resolved_at must not be stamped until it passes. */
export const OPEN_STATUSES: RequestStatus[] = ["new", "backlog", "decision", "triaged", "progress", "waiting", "validation", "escalated"];

/** Statuses that resolve a request (stamp resolved_at, leave open lists).
 *  Already supported closes without work: the product already did it, and
 *  the closing comment is where the customer learns how. */
export const CLOSED_STATUSES: RequestStatus[] = ["done", "supported", "wontdo"];

export const STATUS_LABEL: Record<RequestStatus, string> = {
  new: "New",
  backlog: "Backlog",
  decision: "Needs decision",
  triaged: "Triaged",
  progress: "In progress",
  waiting: "Waiting on customer",
  validation: "Validation",
  done: "Done",
  supported: "Already supported",
  wontdo: "Not planned",
  escalated: "Escalated Core Eng.",
};

/** What a status means, for the ones whose name alone does not say it. Shown
 *  as a tooltip on the status wherever requests are listed, to staff and
 *  customers alike — so it speaks as Euler ("we"). `customer` overrides the
 *  wording where the portal reader is the subject. */
export const STATUS_HINT: Partial<Record<RequestStatus, { staff: string; customer?: string }>> = {
  decision: { staff: "We're discussing it internally at Euler." },
  triaged: { staff: "We have what we need and can start working on it." },
  validation: {
    staff: "The work is finished and the customer is checking it before we call it done.",
    customer: "The work is finished and is with you to check before we call it done.",
  },
  waiting: { staff: "We're blocked and need information or feedback from the customer.", customer: "We're blocked and need information or feedback from you." },
};

/** The kinds of request, in the order selects show them. */
export const TYPE_ORDER: RequestType[] = ["bug", "feature", "question", "config"];

export const TYPE_LABEL: Record<RequestType, string> = {
  bug: "Bug",
  feature: "Feature request",
  question: "Question",
  config: "Configuration or data change",
};

/** The teams a request can finally belong to, in the order the select shows them. */
export const FINAL_OWNER_ORDER: FinalOwner[] = ["support", "triage", "ce", "core", "product"];

/** The technical-owner filter's "No technical owner" value. Member ids are
 *  slugs of names ([a-z0-9-]), so an underscore can never be one. */
export const NO_TECH_OWNER = "_none";

export const FINAL_OWNER_LABEL: Record<FinalOwner, string> = {
  support: "Support",
  triage: "Triage",
  ce: "Customer Engineering",
  core: "Core Engineering",
  product: "Product decision required",
};

/** Where a request came from, as shown to staff. */
export const SOURCE_LABEL: Record<RequestSource, string> = {
  meeting: "From a meeting",
  slack: "From Slack",
  email: "From email",
  manual: "Created manually",
  portal: "From the customer portal",
};

/** The sources staff may set. `portal` is not among them: it is stamped by
 *  the portal's own create path and means "a customer opened this". */
export const STAFF_SOURCES: RequestSource[] = ["manual", "meeting", "slack", "email"];
